'use client';

import { useEffect, useState } from 'react';

import { CountdownDate } from '@flipster/react/design-utils/countdown-date';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';

import { CountdownItemFragment } from '@web/app/[locale]/campaign/[slug]/query.generated';
import { spacing6, spacing9 } from '@web/app/[locale]/campaign/[slug]/styles';

interface Props {
  countdowns: (CountdownItemFragment | null)[];
}

export const Countdown = ({ countdowns }: Props) => {
  const [index, setIndex] = useState(-1);
  const [isOver, setIsOver] = useState(false);

  const isMounted = useMounted();

  useEffect(() => {
    if (!countdowns || countdowns.length === 0) return;
    const now = new Date();
    let activeIndex = -1;
    for (let i = 0; i < countdowns.length; i++) {
      const countdown = countdowns[i];
      if (countdown?.date && new Date(countdown.date) > now) {
        activeIndex = i;
        break;
      }
    }
    if (activeIndex === -1 && countdowns.length > 0) {
      activeIndex = countdowns.length - 1;
      setIsOver(true);
    } else {
      setIsOver(false);
    }
    setIndex(activeIndex);
  }, [countdowns]);

  const handleCountdownEnd = () => {
    const nextIndex = index + 1;
    if (nextIndex >= countdowns.length) {
      setIsOver(true);
    } else {
      setIndex(nextIndex);
    }
  };

  if (!isMounted) return null;

  const currentData = countdowns[index];
  if (!currentData) return null;

  if (isOver) {
    return (
      <div className={hstack({ color: 'content.state.warning', gap: 8, mb: spacing9 })}>
        <CautionCircleIcon size={16} />
        {currentData?.afterText || 'The campaign has ended'}
      </div>
    );
  }
  return (
    <div
      className={flex({
        align: 'center',
        flexDirection: { base: 'column', tablet: 'row' },
        minW: 'max-content',
        gap: spacing6,
        mb: spacing9,
      })}
    >
      <span>{currentData.label}</span>
      <CountdownDate
        value={new Date(currentData.date).toISOString()}
        format='${dd} days ${hh} hours ${mm} min'
        css={css.raw({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          '& > span': {
            textStyle: 'Display/L',
            color: 'content.default.level-4',
          },
        })}
        onEnd={handleCountdownEnd}
      />
    </div>
  );
};
